export default function Introduction() {
  return (
    <section id="introduction" className="py-14 md:py-24 px-4 md:px-6 bg-white">
      <div className="max-w-3xl mx-auto">

        {/* 아티클 헤더 */}
        <div className="mb-8 md:mb-12">
          <span className="text-xs font-bold tracking-widest text-teal-600 uppercase">Our Story</span>
          <h2 className="mt-3 text-2xl md:text-5xl font-black text-gray-950 leading-tight">
            서두르지 않는 마무리.<br />
            <span className="text-teal-600 whitespace-nowrap">두번손안가게 소통하는 이사.</span>
          </h2>
          <div className="flex flex-wrap items-center gap-3 mt-5 text-xs md:text-sm text-gray-400">
            <span className="flex items-center gap-1.5">
              <i className="ri-shield-check-line text-teal-500"></i>
              100% 직영 운영
            </span>
            <span className="w-1 h-1 rounded-full bg-gray-300"></span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line text-teal-500"></i>
              군사관이사
            </span>
            <span className="w-1 h-1 rounded-full bg-gray-300"></span>
            <span className="flex items-center gap-1.5">
              <i className="ri-map-pin-line text-teal-500"></i>
              장거리 이사 환영
            </span>
          </div>
        </div>

        {/* 구분선 */}
        <div className="flex items-center gap-4 mb-8 md:mb-12">
          <div className="h-px flex-1 bg-gray-200"></div>
          <span className="text-gray-300 text-xs tracking-widest">CHAPTER 01</span>
          <div className="h-px flex-1 bg-gray-200"></div>
        </div>

        {/* 스토리 1 - 창업 철학 */}
        <div className="grid md:grid-cols-5 gap-5 md:gap-8 items-center mb-10 md:mb-14">
          <div className="md:col-span-3">
            <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="w-7 h-7 flex items-center justify-center bg-teal-600 text-white text-xs font-bold rounded-full shrink-0">01</span>
              지역 기반, 직영으로만
            </h3>
            <p className="text-gray-600 leading-7 md:leading-8 text-sm md:text-[15px]">
              우리집 익스프레스는 외주를 쓰지 않습니다. 처음 접수부터 마지막 짐 정리까지,
              <strong className="text-gray-800"> 저희 직영 팀이 직접 책임집니다.</strong>
            </p>
            <p className="text-gray-600 leading-7 md:leading-8 text-sm md:text-[15px] mt-3">
              외주 업체를 거치면 가격은 올라가고 책임은 흐릿해집니다.
              우리는 처음부터 그 방식을 택하지 않았습니다.
              고객의 짐이 곧 고객의 일상이라는 생각으로, 매번 처음처럼 임합니다.
            </p>
          </div>
          <div className="md:col-span-2 rounded-xl overflow-hidden h-44 md:h-52">
            <img
              src="https://public.readdy.ai/ai/img_res/47f2cbcb7ddec7fbcb0170537639647f.jpg"
              alt="직영 이사팀"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>

        {/* pull quote */}
        <blockquote className="border-l-4 border-teal-500 pl-5 py-2 my-8 md:my-10 bg-teal-50 rounded-r-xl">
          <p className="text-teal-800 text-base md:text-lg font-semibold leading-relaxed italic">
            &ldquo;고객의 짐은 곧 고객의 일상입니다.
            우리집 익스프레스는 그 무게를 가볍게 여긴 적이 없습니다.&rdquo;
          </p>
        </blockquote>

        {/* 구분선 */}
        <div className="flex items-center gap-4 my-8 md:my-12">
          <div className="h-px flex-1 bg-gray-200"></div>
          <span className="text-gray-300 text-xs tracking-widest">CHAPTER 02</span>
          <div className="h-px flex-1 bg-gray-200"></div>
        </div>

        {/* 스토리 2 - 서비스 범위 */}
        <div className="grid md:grid-cols-5 gap-5 md:gap-8 items-center mb-10 md:mb-14">
          <div className="md:col-span-2 rounded-xl overflow-hidden h-44 md:h-52 order-last md:order-first">
            <img
              src="https://readdy.ai/api/search-image?query=aerial%20view%20of%20South%20Korea%20highway%20landscape%20from%20Gangwon%20mountains%20to%20Busan%20coast%2C%20scenic%20road%20trip%2C%20moving%20truck%20on%20the%20road%2C%20warm%20golden%20hour%2C%20clean%20sky%2C%20lush%20green%20mountains%2C%20minimalist%20travel%20feel&width=400&height=280&seq=intro02&orientation=landscape"
              alt="강원도에서 부산까지"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="md:col-span-3">
            <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <span className="w-7 h-7 flex items-center justify-center bg-teal-600 text-white text-xs font-bold rounded-full shrink-0">02</span>
              강원도에서 부산까지, 직접 갑니다
            </h3>
            <p className="text-gray-600 leading-7 md:leading-8 text-sm md:text-[15px]">
              수도권은 물론, <strong className="text-gray-800">강원도 산간 지역부터 부산 해안가</strong>까지 —
              우리집 익스프레스는 전국 어디든 직영 팀이 직접 출동합니다.
            </p>
            <p className="text-gray-600 leading-7 md:leading-8 text-sm md:text-[15px] mt-3">
              거리에 따라 외주를 쓰거나 비용이 급격히 오르는 일 없이,
              출발지부터 도착지까지 동일한 팀, 동일한 기준으로 진행됩니다.
            </p>
          </div>
        </div>

        {/* 구분선 */}
        <div className="flex items-center gap-4 my-8 md:my-12">
          <div className="h-px flex-1 bg-gray-200"></div>
          <span className="text-gray-300 text-xs tracking-widest">CHAPTER 03</span>
          <div className="h-px flex-1 bg-gray-200"></div>
        </div>

        {/* 스토리 3 - 서비스 종류 */}
        <div className="mb-10 md:mb-14">
          <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <span className="w-7 h-7 flex items-center justify-center bg-teal-600 text-white text-xs font-bold rounded-full shrink-0">03</span>
            합리적인 가격, 최상의 이사 서비스
          </h3>
          <p className="text-gray-600 leading-7 md:leading-8 text-sm md:text-[15px] mb-6">
            포장이사, 일반이사, 사무실이사, 보관이사 등 어떤 형태의 이사든 가능합니다.
            정직함과 성실함으로, 고객이 언제나 편히 찾을 수 있는 이사 파트너가 되겠습니다.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { icon: 'ri-home-4-line', label: '가정이사', desc: '포장·명품·일반이사' },
              { icon: 'ri-building-2-line', label: '사무실이사', desc: '빌딩·공장·대형 이사' },
              { icon: 'ri-archive-line', label: '보관이사', desc: '단기·장기 보관 가능' },
              { icon: 'ri-truck-line', label: '원룸이사', desc: '빠르고 합리적인 이사' },
            ].map((item) => (
              <div key={item.label} className="border border-gray-100 rounded-xl p-4 hover:border-teal-200 hover:bg-teal-50 transition-colors">
                <div className="w-9 h-9 flex items-center justify-center text-teal-600 mb-2">
                  <i className={`${item.icon} text-xl`}></i>
                </div>
                <div className="font-semibold text-gray-900 text-sm mb-0.5">{item.label}</div>
                <div className="text-xs text-gray-500">{item.desc}</div>
              </div>
            ))}
          </div>
        </div>

        {/* 마무리 구분선 + CTA */}
        <div className="flex items-center gap-4 mb-8">
          <div className="h-px flex-1 bg-gray-200"></div>
          <i className="ri-double-quotes-r text-gray-300 text-lg"></i>
          <div className="h-px flex-1 bg-gray-200"></div>
        </div>

        <div className="text-center">
          <p className="text-gray-500 text-sm mb-4">
            지금 바로 견적을 받아보세요. 빠르고 정확하게 답변드립니다.
          </p>
          <a
            href="https://talk.naver.com/profile/wfu3m6i"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-500 text-white px-6 py-2.5 rounded-lg font-semibold text-sm transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-file-list-3-line"></i>
            무료 견적 신청하기
          </a>
        </div>

      </div>
    </section>
  );
}
